import lodash from 'lodash'
import { getABTProvider } from '@/plugins/abt/statsig'

import _locator from '@/service-locator'
import LOGGING_EVENTS from '@/constants/logging-events'
import Config from '@/config'

const abtProvider = getABTProvider()
const APP_NAME = 'checkout_frontend'
const DEBUG_LOGGER_FEATURE_FLAG = 'newrelic_debug_logs_enabled'

const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  TRACE: 'trace',
}

let clientLogger = null
window.loggerDataLayer = []

const parseExtra = (extra) => {
  try {
    if (!extra) return {}
    if (lodash.isString(extra) || lodash.isNumber(extra)) return { extra }
    if (lodash.isObject(extra) && lodash.isObject(extra?.data ?? null))
      return extra?.data ?? {}

    if (lodash.isObject(extra)) return extra

    return { extra: JSON.stringify(extra) }
  } catch (err) {
    return {}
  }
}

const setDefaultAttributes = (window) => {
  const defaultAttributes = getDefaultAttributes(window)
  setCustomAttributes(defaultAttributes)
}

const setCustomAttributes = (customAttributes = {}) => {
  if (!clientLogger) {
    return
  }
  Object.keys(customAttributes).forEach((key) => {
    clientLogger.setCustomAttribute(key, customAttributes[key])
  })
}

const getSessionData = (store = {}) => {
  const { getters = {}, state = {} } = store
  const session = getters['session/order'] || {}
  const pageType = state.tracking?.pageType || ''

  return {
    session,
    orderStep: getters['checkout/currentStep'] || pageType,
  }
}

const getDefaultAttributes = (window, extra) => {
  const queryString = window.location?.search || ''
  const queryStringObj = Object.fromEntries(new URLSearchParams(queryString))
  const { session, orderStep } = getSessionData(_locator.store)

  const attributes = {
    ...lodash.omit(parseExtra(extra), ['message']),
    appName: APP_NAME,
    queryString,
    orderCountryCode: session?.countryCode || '',
    locale: session?.locale || queryStringObj?.locale,
    merchantCountry: session?.merchantCountry || '',
    merchantId: session?.merchantId || '',
    merchantName: session?.merchantName || '',
    orderId: session?.orderId || queryStringObj?.orderId,
    orderStep,
    orderStatus: _locator?.store?.getters['checkout/currentStatus'] ?? 'N/A',
  }

  if (_locator?.store?.getters['payment/hasMetaData'] ?? false) {
    attributes.applePayPreference =
      _locator?.store?.getters['payment/applePayPreference'] ?? 'N/A'
  }

  return attributes
}

const isDebugLoggerFeatureEnabled = () => {
  try {
    return abtProvider.isFeatureEnabled(DEBUG_LOGGER_FEATURE_FLAG)
  } catch (err) {
    return false
  }
}

const log = (message, extraAttributes = {}, level = LogLevel.INFO) => {
  try {
    if (level === LogLevel.DEBUG && !isDebugLoggerFeatureEnabled()) {
      return
    }
    const customAttributes = getDefaultAttributes(window, extraAttributes)
    // if client logger is not initialized for some reason most probably slow internet, add to loggerLayer and it will be sent once the plugins is nitialized.
    if (!clientLogger) {
      window?.loggerDataLayer?.push({
        event: message,
        level,
        data: customAttributes,
      })
      return
    }
    clientLogger.log(message, {
      level,
      customAttributes,
    })
  } catch (error) {
    console.warn({ error }, 'NewRelic log function is not available')
  }
}

const makeLoggerMethod =
  (level) =>
  (message, extraAttributes = {}) =>
    log(message, extraAttributes, level)

const noticeError = (error, extraAttributes = {}) => {
  try {
    if (clientLogger) {
      const customAttributes = getDefaultAttributes(window, extraAttributes)
      clientLogger.noticeError(error, customAttributes)
    }
  } catch (error) {
    console.warn({ error }, 'NewRelic noticeError function is not available')
  }
}

const initGlobalTrackingEvents = () => {
  window.addEventListener('online', () => {
    log(LOGGING_EVENTS.CONNECTION_RESTORED)
  })
  window.addEventListener('beforeunload', () => {
    log(LOGGING_EVENTS.LEAVING_CHECKOUT)
  })
}

export const initializeLogger = () => {
  try {
    if (window.newrelic) {
      window.newrelic.setApplicationVersion(
        Config.env + '.' + Config.releaseVersion
      )
      clientLogger = window.newrelic
      setDefaultAttributes(window)
      initGlobalTrackingEvents()
    } else {
      window.addEventListener('newrelic', (evt) => {
        if (evt.detail.loaded) {
          window.newrelic.setApplicationVersion(
            Config.env + '.' + Config.releaseVersion
          )
          clientLogger = window.newrelic
          setDefaultAttributes(window)
          initGlobalTrackingEvents()

          // process loggerDataLayer events
          while (window?.loggerDataLayer?.length > 0) {
            const { event, level, data } = window?.loggerDataLayer?.pop()
            log(event, data, level)
          }
        }
      })
    }
  } catch (error) {
    console.warn({ error }, 'Newrelic initialization failed')
  }
}

export default {
  log,
  noticeError,
  trace: makeLoggerMethod(LogLevel.TRACE),
  debug: makeLoggerMethod(LogLevel.DEBUG),
  error: makeLoggerMethod(LogLevel.ERROR),
  warn: makeLoggerMethod(LogLevel.WARN),
}
